import { isIpadDevice, isSafariBrowser } from '@chiroup/client-core/functions';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnSizingState,
  type ExpandedState,
  type OnChangeFn,
  type Row,
  type RowData,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { ChevronDownIcon, ChevronRightIcon, LoaderCircleIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Checkbox } from '../checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../table';
import { DataTablePagination } from './data-table-pagination';
import {
  DataTableToolbar,
  type BulkAction,
  type ExportConfig,
  type FilterConfig,
} from './data-table-toolbar';

/**
 * Custom flexRender that calls function renderers directly instead of treating
 * them as React components. TanStack's default flexRender uses <Comp {...props} />,
 * which causes React to unmount/mount DOM elements when the function reference
 * changes (e.g., when columns are recreated on re-render). By calling the function
 * directly, the returned JSX is inlined into the parent's render tree and React
 * patches existing DOM elements in place — preserving event targets for double-click
 * detection and avoiding unnecessary DOM churn.
 */
function flexRender<TProps extends object>(
  renderer: React.ReactNode | React.ComponentType<TProps> | ((props: TProps) => React.ReactNode),
  props: TProps
): React.ReactNode {
  if (renderer == null) return null;

  if (typeof renderer === 'function') {
    return (renderer as (props: TProps) => React.ReactNode)(props);
  }

  return renderer as React.ReactNode;
}

// Extend TanStack Table's ColumnMeta to support per-column className
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Additional CSS class applied to both <th> and <td> for this column */
    className?: string;
    /** Text alignment for the entire column (header and cells) */
    align?: 'left' | 'center' | 'right';
  }
}

// =============================================================================
// Types
// =============================================================================

export interface DataTableProps<TData, TValue> {
  /** Column definitions */
  columns: ColumnDef<TData, TValue>[];
  /** Table data */
  data: TData[];

  // Toolbar configuration
  toolbar?:
    | boolean
    | {
        search?:
          | boolean
          | {
              placeholder?: string;
              column?: string;
              /** Controlled value for server-side search */
              value?: string;
              /** Callback for server-side search */
              onChange?: (value: string) => void;
            };
        filters?: FilterConfig[];
        bulkActions?: BulkAction<TData>[];
        viewOptions?: boolean;
        export?: ExportConfig<TData>;
        /** Custom content to render on the left side of toolbar */
        leftSlot?: React.ReactNode;
        /** Custom content to render on the right side of toolbar */
        rightSlot?: React.ReactNode;
      };

  // Pagination configuration
  pagination?:
    | boolean
    | {
        pageSize?: number;
        pageSizeOptions?: number[];
      }
    | {
        /** Enable server-side (manual) pagination */
        manual: true;
        /** Current page index (0-indexed) */
        pageIndex: number;
        /** Items per page */
        pageSize?: number;
        /** Total number of pages */
        pageCount: number;
        /** Total number of items across all pages */
        totalItems: number;
        /** Called when page changes (0-indexed pageIndex) */
        onPageChange: (pageIndex: number) => void;
        /** Called when page size changes */
        onPageSizeChange?: (pageSize: number) => void;
        /** Available page size options */
        pageSizeOptions?: number[];
        /** Render the pagination bar even when everything fits on one page, so the
         *  "Showing X to Y of Z" label and the rows-per-page control stay reachable. */
        alwaysShow?: boolean;
        /** Offer an "All" rows-per-page choice. While `selected`, the caller passes
         *  pageSize = totalItems (and pageCount 1); the trigger reads "All". */
        allOption?: { selected: boolean; onSelect: () => void };
        /** Rows the caller prepends to the first page that are not part of the paged
         *  set (not in `totalItems`, not in `pageCount`). Only the "Showing X to Y of Z"
         *  label counts them. */
        leadingItems?: number;
      };

  /** Rendered inside <tfoot> after the body rows — e.g. a totals row that stays
   *  at the bottom regardless of sort. Caller supplies the <TableRow>/<TableCell>s. */
  footer?: React.ReactNode;

  // Sorting
  /** Enable server-side (manual) sorting with controlled sorting state.
   *  When provided, the table does not sort rows itself — it reports sort
   *  changes via onSortingChange and renders the rows in the order given.
   *  Sorting is also restricted to a single column (no removal, no multi-sort). */
  manualSorting?: {
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
  };

  // Selection
  /** Enable row selection with checkboxes */
  selectable?: boolean;
  /** Callback when selection changes */
  onSelectionChange?: (rows: TData[]) => void;

  // States
  /** Show loading skeleton (initial load with no data) */
  loading?: boolean;
  /** Skeleton rows to draw while `loading`. Match it to the rows the page will
   *  hold so the layout doesn't jump when data lands. Defaults to 5. */
  skeletonRows?: number;
  /** Show a semi-transparent overlay with spinner (page transitions / refetching) */
  fetching?: boolean;
  /** Custom empty state content */
  emptyState?: React.ReactNode;

  // Styling
  /** Additional class names */
  className?: string;
  /** Make header sticky on scroll */
  stickyHeader?: boolean;
  /** Enable alternating row colors (zebra stripes) */
  striped?: boolean;
  /** Row density: compact, default, or comfortable */
  size?: 'default' | 'compact' | 'comfortable';
  /** Table style variant */
  variant?: 'default' | 'minimal';
  /** Remove outer border, rounding, and shadow (for embedding inside another container) */
  borderless?: boolean;
  /** Custom header row class names */
  headerClassName?: string;
  /** Function to compute custom row class names */
  rowClassName?: (row: Row<TData>, index: number) => string;

  // Row expansion
  /** Enable expandable rows */
  expandable?: boolean;
  /** Render function for expanded row content */
  renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  /** Custom function to determine if a row can expand */
  getRowCanExpand?: (row: Row<TData>) => boolean;

  // Column resizing
  /** Enable column resizing by dragging */
  resizable?: boolean;

  // Infinite scroll
  /** Enable infinite scroll mode — hides pagination and uses IntersectionObserver */
  infiniteScroll?: {
    hasMore: boolean;
    onLoadMore: () => void;
    isLoading?: boolean;
  };

  // Callbacks
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void;
}

// =============================================================================
// Constants
// =============================================================================

/** Size classes for different table densities */
const sizeClasses = {
  compact: 'py-1 px-2 text-xs',
  default: 'py-2 px-4 text-sm',
  comfortable: 'py-4 px-4 text-sm',
} as const;

/** Alignment classes for columns */
const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

// =============================================================================
// Component
// =============================================================================

export function DataTable<TData, TValue>({
  columns: userColumns,
  data,
  toolbar,
  pagination,
  manualSorting,
  selectable = false,
  onSelectionChange,
  loading = false,
  skeletonRows = 5,
  fetching = false,
  emptyState,
  className,
  stickyHeader = false,
  striped = false,
  size = 'default',
  variant = 'default',
  borderless = false,
  headerClassName,
  rowClassName,
  expandable = false,
  renderExpandedRow,
  getRowCanExpand,
  resizable = false,
  infiniteScroll,
  onRowClick,
  footer,
}: DataTableProps<TData, TValue>) {
  // ---------------------------------------------------------------------------
  // Misc
  // ---------------------------------------------------------------------------
  const isIOSWebKit = React.useMemo(() => isIpadDevice() && isSafariBrowser(), []);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({});
  const [tableBodyRenderKey, setTableBodyRenderKey] = React.useState(0); // to force re-renders on table body

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  // Add selection and expand columns as needed
  const columns = React.useMemo(() => {
    const cols: ColumnDef<TData, TValue>[] = [];

    // Add selection column if selectable
    if (selectable) {
      const selectColumn: ColumnDef<TData, TValue> = {
        id: '_select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
            onDoubleClick={() => table.toggleAllRowsSelected(false)}
            aria-label="Select all"
            className="translate-y-[2px]"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={value => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="translate-y-[2px]"
            onClick={e => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      };
      cols.push(selectColumn);
    }

    // Add expand column if expandable
    if (expandable) {
      const expandColumn: ColumnDef<TData, TValue> = {
        id: '_expand',
        header: () => null,
        cell: ({ row }) => {
          if (!row.getCanExpand()) return null;

          return (
            <button
              type="button"
              aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
              aria-expanded={row.getIsExpanded()}
              onClick={e => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-dark-gray-600 rounded"
            >
              {row.getIsExpanded() ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          );
        },
        enableSorting: false,
        enableHiding: false,
      };
      cols.push(expandColumn);
    }

    return [...cols, ...userColumns];
  }, [userColumns, expandable, selectable]);

  // Parse toolbar config
  const toolbarConfig = React.useMemo(() => {
    if (!toolbar) return null;

    if (toolbar === true) {
      return { search: true, filters: [], bulkActions: [], viewOptions: true };
    }

    return toolbar;
  }, [toolbar]);

  // Parse pagination config
  const isManualPagination =
    typeof pagination === 'object' && 'manual' in pagination && pagination.manual;

  const paginationConfig = React.useMemo(() => {
    if (!pagination) return null;

    if (pagination === true) {
      return { pageSize: 10, pageSizeOptions: [10, 20, 50, 100] };
    }

    return { pageSize: 10, pageSizeOptions: [10, 20, 50, 100], ...pagination };
  }, [pagination]);

  // Get search column config
  const searchConfig = React.useMemo(() => {
    if (!toolbarConfig?.search) return null;

    if (toolbarConfig.search === true) {
      return { placeholder: 'Search…', column: undefined };
    }

    return toolbarConfig.search;
  }, [toolbarConfig?.search]);

  // ---------------------------------------------------------------------------
  // Table Instance
  // ---------------------------------------------------------------------------

  // Manual pagination config
  const manualPaginationConfig = React.useMemo(() => {
    if (!isManualPagination || typeof pagination !== 'object' || !('manual' in pagination)) {
      return null;
    }

    return pagination;
  }, [isManualPagination, pagination]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: manualSorting ? manualSorting.sorting : sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
      expanded,
      columnSizing,
      ...(manualPaginationConfig && {
        pagination: {
          pageIndex: manualPaginationConfig.pageIndex,
          pageSize: manualPaginationConfig.pageSize ?? 10,
        },
      }),
    },
    enableRowSelection: selectable,
    enableExpanding: expandable,
    enableColumnResizing: resizable,
    columnResizeMode: 'onChange',
    getRowCanExpand: getRowCanExpand ?? (() => expandable),
    onRowSelectionChange: setRowSelection,
    onSortingChange: manualSorting ? manualSorting.onSortingChange : setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onColumnSizingChange: setColumnSizing,
    // Custom global filter that searches ALL columns case-insensitively
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue).toLowerCase();

      // Search through all visible cells in the row
      return row.getAllCells().some(cell => {
        const value = cell.getValue();

        return String(value ?? '')
          .toLowerCase()
          .includes(search);
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel:
      paginationConfig && !isManualPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: expandable ? getExpandedRowModel() : undefined,
    // Manual (server-side) sorting — table reports changes but renders rows as-is.
    ...(manualSorting && {
      manualSorting: true,
      enableSortingRemoval: false,
      enableMultiSort: false,
    }),
    // Manual (server-side) pagination
    ...(manualPaginationConfig && {
      manualPagination: true,
      pageCount: manualPaginationConfig.pageCount,
      onPaginationChange: (updater: any) => {
        const prev = {
          pageIndex: manualPaginationConfig.pageIndex,
          pageSize: manualPaginationConfig.pageSize ?? 10,
        };
        const next = typeof updater === 'function' ? updater(prev) : updater;

        if (next.pageIndex !== prev.pageIndex) {
          manualPaginationConfig.onPageChange(next.pageIndex);
        }

        if (next.pageSize !== prev.pageSize && manualPaginationConfig.onPageSizeChange) {
          manualPaginationConfig.onPageSizeChange(next.pageSize);
        }
      },
    }),
    initialState:
      paginationConfig && !isManualPagination
        ? {
            pagination: {
              pageSize: paginationConfig.pageSize,
            },
          }
        : undefined,
  });

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Smooth fade transition when page changes
  const pageIndex = table.getState().pagination.pageIndex;
  const tbodyRef = React.useRef<HTMLTableSectionElement>(null);
  const initialPageRef = React.useRef(pageIndex);

  React.useEffect(() => {
    // Skip the initial render
    if (initialPageRef.current === pageIndex && !tbodyRef.current?.dataset.hasNavigated) return;
    const tbody = tbodyRef.current;
    if (!tbody) return;
    tbody.dataset.hasNavigated = '1';
    tbody.style.opacity = '0';
    tbody.style.transition = 'none';
    // Force reflow so opacity:0 is painted before transitioning
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    tbody.offsetHeight;
    tbody.style.transition = 'opacity 200ms ease-in-out';
    tbody.style.opacity = '1';
  }, [pageIndex]);

  React.useEffect(() => {
    if (!isIOSWebKit) return;

    // force tbody remount on data replacement for iOS Safari
    setTableBodyRenderKey(key => key + 1);
  }, [data, isIOSWebKit]);

  // Notify parent of selection changes
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    // Skip initial render to avoid calling with empty selection on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, onSelectionChange, table]);

  // Infinite scroll sentinel
  const sentinelRef = React.useRef<HTMLTableRowElement>(null);

  React.useEffect(() => {
    if (!infiniteScroll?.hasMore || infiniteScroll?.isLoading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          infiniteScroll.onLoadMore();
        }
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infiniteScroll?.hasMore, infiniteScroll?.isLoading, infiniteScroll?.onLoadMore]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={cn('space-y-4', className)}>
      {/* Table */}
      <div
        className={cn(
          borderless
            ? 'bg-white dark:bg-dark-gray-800 overflow-hidden'
            : 'rounded-lg border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 shadow-sm overflow-hidden',
          resizable && 'overflow-x-auto'
        )}
      >
        <div role="status" aria-live="polite" className="sr-only">
          {loading ? 'Loading results…' : fetching ? 'Updating results…' : ''}
        </div>
        {/* Toolbar (integrated inside table card) */}
        {toolbarConfig && (
          <DataTableToolbar
            table={table}
            searchConfig={searchConfig}
            filters={toolbarConfig.filters}
            bulkActions={toolbarConfig.bulkActions}
            viewOptions={toolbarConfig.viewOptions}
            exportConfig={toolbarConfig.export}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            leftSlot={toolbarConfig.leftSlot}
            rightSlot={toolbarConfig.rightSlot}
          />
        )}
        {/* Indeterminate progress bar for fetching state */}
        {fetching && !loading && (
          <div className="h-0.5 w-full bg-gray-200 dark:bg-dark-gray-600 overflow-hidden">
            <div
              className="datatable-progress h-full bg-primary-500 dark:bg-primary-400"
              style={{
                width: '40%',
                animation: 'datatable-progress 1.2s ease-in-out infinite',
              }}
            />
            <style>{`
                @keyframes datatable-progress {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(350%); }
                }
                @media (prefers-reduced-motion: reduce) {
                  .datatable-progress { animation: none !important; }
                }
              `}</style>
          </div>
        )}
        <Table
          aria-busy={loading || fetching || undefined}
          style={resizable ? { width: table.getCenterTotalSize() } : undefined}
        >
          {/* Header */}
          <TableHeader
            className={cn(stickyHeader && 'sticky top-0 z-10 bg-white dark:bg-dark-gray-800')}
          >
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow
                key={headerGroup.id}
                className={cn(
                  variant === 'minimal'
                    ? 'border-b border-gray-300 dark:border-dark-gray-600'
                    : 'bg-gray-100/80 dark:bg-dark-gray-700 border-b-2 border-gray-300 dark:border-dark-gray-600',
                  headerClassName
                )}
              >
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      sizeClasses[size],
                      'relative',
                      variant === 'minimal'
                        ? 'text-sm font-medium text-gray-700 dark:text-dark-gray-300'
                        : 'text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-dark-gray-300',
                      header.column.columnDef.meta?.align &&
                        alignClasses[header.column.columnDef.meta.align],
                      header.column.columnDef.meta?.className
                    )}
                    style={resizable ? { width: header.getSize() } : undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {/* Column resize handle */}
                    {resizable && header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                          'hover:bg-primary-500 dark:hover:bg-primary-400',
                          header.column.getIsResizing() && 'bg-primary-500 dark:bg-primary-400'
                        )}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          {/* Body */}
          <TableBody
            key={isIOSWebKit ? tableBodyRenderKey : undefined} // force re-render on iOS Safari to avoid stale paint issues when data is replaced
            ref={tbodyRef}
            className={cn(
              'transition-opacity duration-200 ease-out',
              fetching && !loading && 'opacity-50 pointer-events-none'
            )}
          >
            {loading ? (
              // Loading skeleton
              Array.from({ length: Math.max(1, skeletonRows) }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`} className={sizeClasses[size]}>
                      <div className="h-4 w-full animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-dark-gray-600" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              // Data rows
              table.getRowModel().rows.map((row, rowIndex) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    className={cn(
                      'transition-colors duration-300 ease-out',
                      onRowClick && 'cursor-pointer',
                      row.getIsSelected() && 'bg-primary-50 dark:bg-primary-900/20',
                      striped && rowIndex % 2 === 1
                        ? 'bg-gray-50/80 dark:bg-dark-gray-700/40'
                        : 'bg-white dark:bg-dark-gray-800',
                      'hover:bg-gray-100 dark:hover:bg-dark-gray-700',
                      rowClassName?.(row, rowIndex)
                    )}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          sizeClasses[size],
                          cell.column.columnDef.meta?.align &&
                            alignClasses[cell.column.columnDef.meta.align],
                          cell.column.columnDef.meta?.className
                        )}
                        style={resizable ? { width: cell.column.getSize() } : undefined}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Expanded row content */}
                  {expandable && row.getIsExpanded() && renderExpandedRow && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="bg-gray-50 dark:bg-dark-gray-700/40"
                      >
                        {renderExpandedRow(row)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              // Empty state
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {/* Sticky + viewport-capped so the empty state stays visible when the
                      table is wider than its horizontally scrolling container. */}
                  <div className="sticky left-0 w-full max-w-[calc(100vw-2rem)]">
                    {emptyState || (
                      <div className="flex flex-col items-center justify-center px-4 py-8 text-gray-500 dark:text-dark-gray-400">
                        <svg
                          className="h-12 w-12 mb-4 text-gray-300 dark:text-dark-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                          />
                        </svg>
                        <p className="text-sm font-medium">No results found</p>
                        <p className="text-sm mt-1">Try adjusting your search or filters.</p>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {/* Infinite scroll sentinel */}
            {infiniteScroll && !loading && (
              <>
                {infiniteScroll.hasMore && (
                  <tr ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
                )}
                {infiniteScroll.isLoading && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center py-4">
                      <LoaderCircleIcon
                        role="img"
                        aria-label="Loading more results"
                        className="h-5 w-5 animate-spin motion-reduce:animate-none mx-auto text-gray-400"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
          {footer && !loading && <TableFooter>{footer}</TableFooter>}
        </Table>

        {/* Pagination - only show when there's more than one page (or the caller asked for it always) */}
        {!infiniteScroll &&
          paginationConfig &&
          (manualPaginationConfig
            ? manualPaginationConfig.alwaysShow ||
              manualPaginationConfig.totalItems > (manualPaginationConfig.pageSize ?? 10)
            : table.getFilteredRowModel().rows.length > paginationConfig.pageSize) && (
            <DataTablePagination
              table={table}
              pageSizeOptions={paginationConfig.pageSizeOptions}
              totalItems={manualPaginationConfig?.totalItems}
              showPageSizeSelector={
                !manualPaginationConfig || !!manualPaginationConfig.onPageSizeChange
              }
              allOption={manualPaginationConfig?.allOption}
              leadingItems={manualPaginationConfig?.leadingItems}
            />
          )}
      </div>
    </div>
  );
}

// =============================================================================
// Exports
// =============================================================================

// Re-export TanStack types
export type { ColumnDef, Row, SortingState } from '@tanstack/react-table';

// Re-export sub-components
export { DataTableColumnHeader } from './data-table-column-header';
export { buildColumns } from './data-table-column-schema';
export { EditableCell, type EditableCellProps } from './data-table-editable-cell';
export { DataTableRowActions, type RowAction } from './data-table-row-actions';

// Re-export toolbar types
export type { BulkAction, ExportConfig, FilterConfig } from './data-table-toolbar';

// =============================================================================
// Status pill helper
// =============================================================================

/**
 * Full-string class map so Tailwind JIT can statically scan every token.
 * Never build these via template literals — the scanner won't find them and
 * the classes will be purged in production.
 */
const STATUS_PILL_CLASSES: Record<string, string> = {
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  lime: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  pink: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
  stone: 'bg-stone-100 text-stone-700 dark:bg-stone-900/30 dark:text-stone-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  zinc: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-300',
};

/**
 * Returns the Tailwind classes for a colored status pill badge.
 * Falls back to gray for unknown color tokens.
 */
export const statusPill = (color: string): string =>
  STATUS_PILL_CLASSES[color] ?? STATUS_PILL_CLASSES['gray'];
